import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import truncate from "truncate-html";
import image_search from "../../../assets/img/images.png";
import image_illustration from "../../../assets/img/illustration.png";
import "../../../assets/css/SearchResultSection.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const YOUTUBE_URL = process.env.REACT_APP_YOUTUBE_URL;
const GOOGLE_URL = process.env.REACT_APP_GOOGLE_URL;

const validPath = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value !== "undefined" &&
  value !== "null";

const uploadUrl = (path) =>
  validPath(path) ? `${BACKEND_URL}upload/${path}` : null;

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return { month: "---", day: "--", year: "----" };
  }
  return {
    month: date.toLocaleString("en", { month: "short" }).toUpperCase(),
    day: date.getDate(),
    year: date.getFullYear(),
  };
};

function SearchResultSection({
  datacontent = [],
  downloadSection = [],
  pagination,
  setPageRead,
  paginationDownload,
  setPageDownload,
}) {
  const changePage = useCallback((setter, page, lastPage) => {
    if (
      typeof setter !== "function" ||
      page < 1 ||
      page > Number(lastPage || 1)
    )
      return;
    const scrollPosition = window.scrollY;
    setter(page);
    window.requestAnimationFrame(() =>
      window.scrollTo({ top: scrollPosition, behavior: "auto" }),
    );
  }, []);

  const renderPagination = (data, setter, label) => {
    const current = Number(data?.current_page || 1);
    const last = Number(data?.last_page || 1);
    if (last <= 1) return null;
    return (
      <nav className="sr-pagination" aria-label={label}>
        <span>
          Page <strong>{current}</strong> of <strong>{last}</strong>
        </span>
        <div className="sr-pagination-controls">
          <button
            type="button"
            onClick={() => changePage(setter, 1, last)}
            disabled={current <= 1}
            aria-label="First page"
          >
            «
          </button>
          <button
            type="button"
            onClick={() => changePage(setter, current - 1, last)}
            disabled={current <= 1}
            aria-label="Previous page"
          >
            ‹
          </button>
          <span className="sr-page-current">{current}</span>
          <button
            type="button"
            onClick={() => changePage(setter, current + 1, last)}
            disabled={current >= last}
            aria-label="Next page"
          >
            ›
          </button>
          <button
            type="button"
            onClick={() => changePage(setter, last, last)}
            disabled={current >= last}
            aria-label="Last page"
          >
            »
          </button>
        </div>
      </nav>
    );
  };

  const items = Array.isArray(datacontent) ? datacontent : [];
  const downloads = Array.isArray(downloadSection) ? downloadSection : [];

  return (
    <section id="service-details" className="sr-section section">
      <div className="container">
        <header className="sr-heading">
          <span className="sr-eyebrow">SEARCH &amp; DISCOVER</span>
          <h2>Search Results</h2>
          <span className="sr-heading-accent" />
          {/* <p>Explore news, announcements, videos and public information from the Ministry.</p> */}
        </header>

        <div className="sr-layout">
          <main className="sr-results">
            {items.length === 0 ? (
              <div className="sr-empty-state">
                <i className="bi bi-search" aria-hidden="true" />
                <h3>No results found</h3>
                <p>There are no matching items to display at the moment.</p>
              </div>
            ) : (
              items.map((service, index) => {
                const date = formatDate(service.daterqw);
                const parts = validPath(service.addressLink)
                  ? service.addressLink.split("*Locat*")
                  : [];
                const provider = (parts[1] || "").trim().toLowerCase();
                const mediaId = parts[0] || "";
                const isVideo = service.columes === "title_advert_utube";
                const isDocument = service.columes === "title_show_echo_01wa";
                const isMedia = isVideo || isDocument;
                const imageUrl = uploadUrl(service.passport);
                const externalUrl =
                  provider === "youtube"
                    ? `${YOUTUBE_URL || ""}${mediaId}`
                    : `${GOOGLE_URL || ""}${mediaId}`;
                const title = service.title || "Ministry update";

                return (
                  <article
                    className="sr-result-card"
                    key={service.id || `${service.columes}-${index}`}
                  >
                    <div className="sr-result-main">
                      <div className="sr-result-copy">
                        <div className="sr-result-meta">
                          <span className="sr-category">
                            {isVideo
                              ? "Media Spotlight"
                              : isDocument
                                ? "Public Resource"
                                : "Ministry Update"}
                          </span>
                          <span className="sr-meta-dot" />
                          <span>
                            {date.day} {date.month} {date.year}
                          </span>
                        </div>

                        <h3 className="sr-result-title">
                          {isMedia ? (
                            <a
                              href={externalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {title}
                            </a>
                          ) : (
                            <Link
                              to={`/read/${service.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <HTMLDisplay
                                html={service.story01 || title}
                                textNo=""
                                type="title"
                              />
                            </Link>
                          )}
                        </h3>

                        {!isVideo && (
                          <div className="sr-result-description">
                            {isDocument ? (
                              <p>
                                {service.caption ||
                                  "Open this resource to view more information."}
                              </p>
                            ) : (
                              <Link
                                to={`/read/${service.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="sr-description-link"
                              >
                                <HTMLDisplay
                                  html={service.story01 || ""}
                                  textNo={30}
                                  type="body"
                                />
                              </Link>
                            )}
                          </div>
                        )}

                        {isMedia ? (
                          <a
                            className="sr-read-link"
                            href={
                              isDocument
                                ? uploadUrl(service.addressLink)
                                : externalUrl
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {isVideo ? "Watch media" : "Open resource"}{" "}
                            <span aria-hidden="true">→</span>
                          </a>
                        ) : service.id ? (
                          <Link
                            className="sr-read-link"
                            to={`/read/${service.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Read full story <span aria-hidden="true">→</span>
                          </Link>
                        ) : null}
                      </div>

                      <div className="sr-result-media">
                        {isVideo ? (
                          <div className="sr-video-frame">
                            {provider === "youtube" && mediaId ? (
                              <iframe
                                src={`${YOUTUBE_URL || ""}${mediaId}`}
                                title={title}
                                loading="lazy"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                              />
                            ) : (
                              <a
                                href={externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="sr-media-fallback"
                              >
                                <img
                                  src={image_search}
                                  alt="Open media item"
                                  loading="lazy"
                                />
                                <span>
                                  <i
                                    className="bi bi-play-circle"
                                    aria-hidden="true"
                                  />{" "}
                                  Open media
                                </span>
                              </a>
                            )}
                          </div>
                        ) : isDocument ? (
                          <a
                            href={uploadUrl(service.addressLink)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sr-resource-preview"
                          >
                            <img
                              src={image_illustration}
                              alt={title}
                              loading="lazy"
                              decoding="async"
                            />
                            <span>
                              <i
                                className="bi bi-file-earmark-arrow-down"
                                aria-hidden="true"
                              />{" "}
                              View resource
                            </span>
                          </a>
                        ) : imageUrl ? (
                          <Link
                            to={`/read/${service.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sr-result-image"
                          >
                            <img
                              src={imageUrl}
                              alt={title}
                              loading="lazy"
                              decoding="async"
                            />
                            <span className="sr-image-date">
                              <span>{date.month}</span>
                              <strong>{date.day}</strong>
                              <span>{date.year}</span>
                            </span>
                          </Link>
                        ) : (
                          <div className="sr-no-image">
                            <i className="bi bi-image" aria-hidden="true" />
                            <span>Ministry Update</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {service.caption && !isVideo && !isDocument && (
                      <div className="sr-caption">
                        <HTMLDisplay
                          html={service.caption}
                          textNo={20}
                          type="title"
                        />
                      </div>
                    )}
                  </article>
                );
              })
            )}
            {renderPagination(
              pagination,
              setPageRead,
              "Search result pagination",
            )}
          </main>

          <aside className="sr-sidebar">
            <section className="sr-sidebar-panel">
              <div className="sr-sidebar-heading">
                <span className="sr-sidebar-icon">
                  <i className="bi bi-folder2-open" aria-hidden="true" />
                </span>
                <div>
                  <span className="sr-sidebar-eyebrow">DOCUMENT CENTRE</span>
                  <h3>Downloadable Resources</h3>
                </div>
              </div>

              {downloads.length === 0 ? (
                <p className="sr-no-downloads">
                  No downloadable resources are available on this page.
                </p>
              ) : (
                <ul className="sr-download-list">
                  {downloads.map((file, index) => {
                    const fileUrl = uploadUrl(file.addressLink);
                    if (!fileUrl) return null;
                    return (
                      <li key={file.id || index}>
                        <a
                          href={fileUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sr-download-item"
                        >
                          <span className="sr-file-icon">
                            <i
                              className="bi bi-file-earmark-pdf"
                              aria-hidden="true"
                            />
                          </span>
                          <span className="sr-file-copy">
                            <strong>
                              {truncate(file.title || "Download document", 12, {
                                byWords: true,
                              })}
                            </strong>
                            <small>Download document</small>
                          </span>
                          <i
                            className="bi bi-download sr-download-action"
                            aria-hidden="true"
                          />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
              {renderPagination(
                paginationDownload,
                setPageDownload,
                "Download pagination",
              )}
            </section>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default React.memo(SearchResultSection);
